import axios from 'axios';
import {API_URL} from '../../../tenant/apiBase'
import {
    IReportSalesModel,
    ITotalSalesRow,
    ITransactionReportModel,
    ITotalTransactionRow,
    IInTransitReportModel,
    ITotalInTransitRow,
} from './_models';
import { StockApiResponse } from '../stock/core/_models'; // Correct relative path

// Define the interface if not imported from _models.ts
// This should match the actual API response structure
interface IMonthlyPaymentItem {
    supplier_payment_id: number; // Use actual unique ID
    supplier_id: number;
    net: string; // Use 'net' field, type is string
    month: number;
    year: number;
    status: string;
    invoice: string | null;
    date_added: string;
    supplier_name: string;
    month_name: string; 
    status_text: string; 
    [key: string]: any; // Allow other properties
}

// Define an interface for the expected upload response structure
interface IUploadInvoiceResponse {
    message?: string; // Message is optional
    // Add other potential fields from the response if known
    [key: string]: any; // Allow other properties
}


export const GET_SALES_REPORT = `${API_URL}/api/partner-portal/supplier-sales`;
export const GET_TRANSACTION_REPORT = `${API_URL}/api/partner-portal/supplier-transaction`;
export const GET_MONTHLY_PAYMENT = `${API_URL}/api/partner-portal/supplier-payment`;
export const GET_PAYMENT_DETAILS = `${API_URL}/api/partner-portal/supplier-payment/details`;
export const GET_PAYMENT_SUMMARY = `${API_URL}/api/partner-portal/supplier-payment/summary`;
export const UPLOAD_PAYMENT_INVOICE = `${API_URL}/api/partner-portal/supplier-payment`; // Base URL, ID will be appended
export const GET_IN_TRANSIT_SALES_REPORT = `${API_URL}/api/partner-portal/in-transit`;
export const GET_STOCK_REPORT = `${API_URL}/api/partner-portal/product/stock`; // Add stock report endpoint
export const GET_PACKAGE_SALES_SUMMARY = `${API_URL}/api/partner-portal/sale/package-sales-summary`;

export interface IPackageSalesSummary {
    packages_sold: number
    units_via_package: number
    units_standalone: number
}

export function getPackageSalesSummary(
    token: string | undefined,
    language: string,
    filterDateStart?: string,
    filterDateEnd?: string
) {
    const qs = new URLSearchParams()
    if (filterDateStart) qs.set('filter_date_start', filterDateStart)
    if (filterDateEnd) qs.set('filter_date_end', filterDateEnd)
    const s = qs.toString()
    return axios.get<{data: IPackageSalesSummary}>(
        `${GET_PACKAGE_SALES_SUMMARY}${s ? `?${s}` : ''}`,
        {headers: {token: token || '', language: language}}
    )
}

export function getSalesReports(
    filtersObj: any,
    token: string | undefined,
    language: string
) {
    return axios.get<{ data: { sales_products: IReportSalesModel[]; month: string; total_sales: ITotalSalesRow } }>(
        `${GET_SALES_REPORT}${filtersObj}${filtersObj === '' ? '?' : '&'}`,
        {headers: {token: token || '', language: language}}
    );
}


export function getTransactionReports(
    filtersObj: any,
    token: string | undefined,
    language: string
) {
    const queryParams = `${filtersObj}${filtersObj === '' ? '?' : '&'}`;

    return axios.get<{
        data: { transaction_products: ITransactionReportModel[]; total_transaction: ITotalTransactionRow }
    }>(
        `${GET_TRANSACTION_REPORT}${queryParams}`,
        {
            headers: {
                token: token || '',
                language: language,
            },
        }
    );
}

export function getStockReport(
    filtersObj: string,
    token: string | undefined,
    language: string
) {
    return axios.get<StockApiResponse>(
        `${GET_STOCK_REPORT}${filtersObj}`,
        {
            headers: {
                token: token || '',
                language: language,
            },
        }
    );
}


// Function to upload the partner invoice
export function uploadInvoice(
    paymentId: number, 
    file: File, 
    token: string | undefined, 
    language: string
) {
    const formData = new FormData();
    formData.append('invoice', file); // 'invoice' is the expected field name on the backend

    // Construct the specific endpoint URL for the payment ID as per user feedback
    const UPLOAD_URL = `${UPLOAD_PAYMENT_INVOICE}/${paymentId}/invoice`; 

    // Expect the response data itself to match IUploadInvoiceResponse
    return axios.post<IUploadInvoiceResponse>( 
        UPLOAD_URL,
        formData,
        {
            headers: {
                'Content-Type': 'multipart/form-data', // Important for file uploads
                token: token || '',
                language: language,
            },
        }
    );
}


// Function to fetch the invoice path
export function getInvoiceFile(
    paymentId: number, 
    token: string | undefined, 
    language: string
) {
    // Construct the specific endpoint URL for the payment ID
    const VIEW_URL = `${UPLOAD_PAYMENT_INVOICE}/${paymentId}/invoice`; 

    // Expect a JSON response with invoice_path directly in the data object
    return axios.get<{ invoice_path: string }>( 
        VIEW_URL,
        {
            headers: {
                token: token || '',
                language: language,
            },
            // No responseType: 'blob' needed, default is JSON
        }
    );
}


// Function to fetch detailed payment report for export. A payment is read by its id: payments
// follow the supplier's own schedule (a week, a month…), so one month can hold several and
// month+year would export all of them. month/year stay as the fallback for an older backend.
export function getPaymentDetailsReport(paymentId: number, month: number, year: number, token: string | undefined, language: string) {
    const params = { payment_id: paymentId, month, year };
    // Update type to include transactions and summary objects based on actual response
    return axios.get<{
        data: {
            transactions: any[];
            pickup_fees: { type: string; total_pickup_fees_net: string };
            availability_fees: { type: string; total_availability_fees_net: string };
            totals: {
                total_gross_sales: number;
                total_dumyah_fees: number;
                total_additional_fees: string;
                net_payable_to_partner_after_fees: number;
            };
        }
    }>(
        GET_PAYMENT_DETAILS,
        {
            params: params,
            headers: {
                token: token || '',
                language: language,
            },
        }
    );
}

export function getInTransitReports(
    filtersObj: any,
    token: string | undefined,
    language: string
) {

    const queryParams = `${filtersObj}${filtersObj === '' ? '?' : '&'}`;
    return axios.get<{ data: { in_transit_products: IInTransitReportModel[]; total_sales: ITotalInTransitRow } }>(
        `${GET_IN_TRANSIT_SALES_REPORT}${queryParams}`,
        {
            headers: {
                token: token || '',
                language: language,
            },
        }
    );
}


export function getMonthlyPayments(
    filtersObj: any,
    token: string | undefined,
    language: string
) {
    const queryParams = filtersObj ? `?${filtersObj}` : '';

    // Update the expected response structure to match the actual API
    // It includes pagination info alongside the data array
    return axios.get<{ 
        data: { 
            data: IMonthlyPaymentItem[], // The actual array of payments
            // Add other pagination fields if needed for typing
            current_page: number;
            first_page_url: string;
            from: number;
            last_page: number;
            last_page_url: string;
            links: any[]; // Define link type more specifically if needed
            next_page_url: string | null;
            path: string;
            per_page: number;
            prev_page_url: string | null;
            to: number;
            total: number;
        } 
    }>(
        `${GET_MONTHLY_PAYMENT}${queryParams}`,
        {
            headers: {
                token: token || '',
                language: language,
            },
        }
    );
}

// ─────────────────────────────────────────────────────────────────
// Settlements summary — drives the top-of-page stat cards on the
// Payments page (YTD, last paid, next payout, held).
// Backend: GET /api/partner-portal/supplier-payment/summary
// ─────────────────────────────────────────────────────────────────

export interface ISettlementSummary {
  currency: string
  payment_terms: {
    label: string
    days: number
  }
  /** When the supplier's payment statements are issued ("Weekly on Saturday"); absent on an older backend. */
  payment_schedule?: {
    label: string
    next_issue_date: string | null
  }
  ytd: {
    year: number
    gross_sales: number
    fees: number
    net_paid: number
    net_pending: number
  }
  last_paid: {
    supplier_payment_id: number
    net: number
    month: number
    year: number
    month_name: string
    invoice_uploaded_at: string | null
  } | null
  next: {
    supplier_payment_id: number
    net: number
    month: number
    year: number
    month_name: string
    status: string
    status_text: string
    invoice_uploaded_at: string | null
    estimated_paid_at: string | null
  } | null
  held: {
    total: number
    count: number
    by_status: {
      status: string
      status_text: string
      count: number
      total: number
    }[]
  }
}

export function getSettlementSummary(
  token: string | undefined,
  language: string,
  year?: number,
) {
  const qs = year ? `?year=${year}` : ''
  return axios.get<{data: ISettlementSummary}>(`${GET_PAYMENT_SUMMARY}${qs}`, {
    headers: {token: token || '', language},
  })
}
